/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import update from "immutability-helper"
import { createReducer } from "store/create-reducer"
import { AxiosAction } from "order-manager/types/api/common.type"
import { FetchStoreListResponse } from "order-manager/types/api/orderFetchStoreList.type"
import { getPortalUrl, getApiUrl } from "constants/domain"
import { Store } from "order-manager/types/models/store"
import { FetchMaxAPIStoreDetailResponse } from "order-manager/types/api/orderFetchMaxAPIStoreDetail.type"
import {
  FetchStoreOpenStatusResponse,
  UpdateStoreStatusParams,
} from "order-manager/types/api/orderStoreOpenStatus.type"
import { FetchCityListResponse } from "order-manager/types/api/orderFetchCityList.type"
import { TroyWebTracker } from "utils/tracking"
import { EventCategory, EventAction } from "order-manager/constants/tracking"
import { OrderManagerMerchantStoreState } from "./merchantstore.type"

const NAMESPACE = "ORDER_MANAGER_MERCHANT"

// file constant
const portalUrl = getPortalUrl()
const apiUrl = getApiUrl()
const actionSuccess = (action: string) => `${action}_SUCCESS`
const actionFail = (action: string) => `${action}_FAIL`

// actions
export const actions = {
  RESET_STORE_LIST: `${NAMESPACE}/RESET_STORE_LIST`,
  FETCH_STORE_LIST: `${NAMESPACE}/FETCH_STORE_LIST`,
  SEARCH_STORE: `${NAMESPACE}/SEARCH_STORE`,
  FETCH_CITY_LIST: `${NAMESPACE}/FETCH_CITY_LIST`,
  FETCH_STORE_FEATURE_FLAG: `${NAMESPACE}/FETCH_STORE_FEATURE_FLAG`,
  FETCH_STORE_DETAIL_FROM_MAX_API: `${NAMESPACE}/FETCH_STORE_DETAIL_FROM_MAX_API`,
  RESET_STORE_DETAIL_FROM_MAX_API: `${NAMESPACE}/RESET_STORE_DETAIL_FROM_MAX_API`,
  SET_STORE_FEATURE_FLAG: `${NAMESPACE}/SET_STORE_FEATURE_FLAG`,
  FETCH_STORE_OPEN_STATUS: `${NAMESPACE}/FETCH_STORE_OPEN_STATUS`,
  UPDATE_STORE_STATUS: `${NAMESPACE}/UPDATE_STORE_STATUS`,
}

type ActionTypes =
  | typeof actions.RESET_STORE_LIST
  | typeof actions.FETCH_STORE_LIST
  | typeof actions.FETCH_STORE_DETAIL_FROM_MAX_API
  | typeof actions.SEARCH_STORE
  | typeof actions.FETCH_STORE_OPEN_STATUS

// action creators
const resetStoreList = () => ({
  type: actions.RESET_STORE_LIST,
})

const fetchStoreList = ({
  countryCode,
  offset,
  limit,
  append = false,
  isLoadMore = false,
}: {
  countryCode: string
  offset: number
  limit: number
  append?: boolean
  isLoadMore?: boolean
}) => ({
  type: actions.FETCH_STORE_LIST,
  notTrackError: true, // trackError middleware exception
  payload: {
    append,
    isLoadMore,
    request: {
      url: `${portalUrl}/foodtroy/v1/${countryCode}/merchant-groups/catalog-stores?offset=${offset}&limit=${limit}`,
      method: "get",
    },
  },
})

const searchStore = ({
  search,
  offset = 0,
  limit = 100,
  includeItemsWithoutPhotosCount = false,
  includeInactive = true,
  isFetchFeatureFlag = false,
  merchantID,
  city = [],
  asc = true,
}: {
  search: string
  offset?: number
  limit?: number
  includeItemsWithoutPhotosCount?: boolean
  includeInactive?: boolean
  isFetchFeatureFlag?: boolean
  merchantID?: string
  city: string[]
  asc?: boolean
}) => ({
  type: isFetchFeatureFlag
    ? actions.FETCH_STORE_FEATURE_FLAG
    : actions.SEARCH_STORE,
  payload: {
    merchantID,
    request: {
      url: `${apiUrl}/delvplatformapi/merchant/v1/merchant-group/store/search?offset=${offset}&limit=${limit}&search=${search}&includeItemsWithoutPhotosCount=${includeItemsWithoutPhotosCount}&includeInactive=${includeInactive}&cityIDs=${city.toString()}&asc=${asc}`,
      method: "get",
    },
  },
})

const fetchCityList = (includeInactive = true) => ({
  type: actions.FETCH_CITY_LIST,
  payload: {
    request: {
      url: `${apiUrl}/delvplatformapi/merchant/v1/merchant-group/store/citylist?includeInactive=${includeInactive}`,
      method: "get",
    },
  },
})

const fetchStoreDetailFromMaxAPI = ({
  merchantID,
}: {
  merchantID: string
}) => ({
  type: actions.FETCH_STORE_DETAIL_FROM_MAX_API,
  payload: {
    merchantID,
    request: {
      url: `${apiUrl}/food/merchant/v2/merchants`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const resetStoreDetailFromMaxAPI = () => ({
  type: actions.RESET_STORE_DETAIL_FROM_MAX_API,
})

const fetchStoreOpenStatus = ({ merchantID }: { merchantID: string }) => ({
  type: actions.FETCH_STORE_OPEN_STATUS,
  payload: {
    merchantID,
    request: {
      url: `${apiUrl}/food/merchant/v3/open-status`,
      method: "get",
      headers: {
        merchantID,
      },
    },
  },
})

const updateStoreStatus = ({
  merchantID,
  params,
}: {
  merchantID: string
  params: UpdateStoreStatusParams
}) => ({
  type: actions.UPDATE_STORE_STATUS,
  payload: {
    request: {
      url: `${apiUrl}/food/merchant/v1/merchant/status`,
      method: "put",
      data: params,
      headers: { merchantID },
    },
  },
})

const setStoreFeatureFlag = ({
  isMexSupplierSeller,
  isMerchantCanEditOrder,
  enableDownloadMpaTransaction,
}: {
  isMexSupplierSeller: boolean
  isMerchantCanEditOrder: boolean
  enableDownloadMpaTransaction: boolean
}) => ({
  type: actions.SET_STORE_FEATURE_FLAG,
  payload: {
    isMexSupplierSeller,
    isMerchantCanEditOrder,
    enableDownloadMpaTransaction,
  },
})

const mockAction = (actionType: string, data: any) => ({
  type: actionType,
  payload: data,
})

export const actionCreator = {
  resetStoreList,
  fetchStoreList,
  fetchCityList,
  fetchStoreDetailFromMaxAPI,
  resetStoreDetailFromMaxAPI,
  mockAction,
  setStoreFeatureFlag,
  searchStore,
  fetchStoreOpenStatus,
  updateStoreStatus,
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

export const initialState = (): OrderManagerMerchantStoreState => ({
  store: undefined,
  maxAPIStore: undefined,
  storeList: undefined,
  storeListSearch: undefined,
  cityList: undefined,
  merchantID: undefined,
  merchantGroupID: "",
  maxAPIMerchantID: undefined,
  isFetchingStore: false,
  isFetchingMaxAPIStore: true, // prevent blinking in UI first load
  isFetchingStoreList: false,
  isFetchingStoreSearch: false,
  storeListHasMore: false,
  isFetchingMoreStoreList: false,
  isFetchingCityList: false,
  storeFeatureFlag: undefined,
  storeOpenStatus: undefined,
  isMexSupplierSeller: false,
  isMerchantCanEditOrder: false,
  enableDownloadMpaTransaction: false,
})

// reducer
export const reducer = createReducer<
  OrderManagerMerchantStoreState,
  ActionTypes,
  ActionCreatorTypeList
>(initialState(), {
  [actions.RESET_STORE_LIST]: () => {
    return initialState()
  },
  [actions.RESET_STORE_DETAIL_FROM_MAX_API]: state => {
    return update(state, {
      maxAPIStore: { $set: undefined },
      maxAPIMerchantID: { $set: undefined },
      storeOpenStatus: { $set: undefined },
      isMexSupplierSeller: { $set: false },
      isMerchantCanEditOrder: { $set: false },
      enableDownloadMpaTransaction: { $set: false },
    })
  },
  [actions.FETCH_STORE_DETAIL_FROM_MAX_API]: (
    state: OrderManagerMerchantStoreState,
    action: ActionCreatorTypes["fetchStoreDetailFromMaxAPI"],
  ) => {
    const nextState = update(state, {
      maxAPIStore: {
        $set: undefined,
      },
      maxAPIMerchantID: {
        $set: action.payload.merchantID,
      },
      isFetchingMaxAPIStore: {
        $set: true,
      },
    })

    return nextState
  },
  [actionSuccess(actions.FETCH_STORE_DETAIL_FROM_MAX_API)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<
      FetchMaxAPIStoreDetailResponse,
      ActionCreatorTypes["fetchStoreDetailFromMaxAPI"]
    >,
  ) => {
    const merchantID = action.meta.previousAction.payload.merchantID

    if (merchantID !== state.maxAPIMerchantID) return state

    const dt = action?.payload?.data

    if (!dt?.merchant) {
      TroyWebTracker.sendEvent({
        category: EventCategory.orderListPage,
        action: EventAction.loadMaxAPIStoreEmpty,
        labels: {
          MERCHANT_ID: merchantID,
          RESPONSE: dt,
        },
      })
    }

    return update(state, {
      maxAPIStore: {
        $set: dt?.merchant ? dt : undefined,
      },
      isFetchingMaxAPIStore: {
        $set: false,
      },
    })
  },
  [actionFail(actions.FETCH_STORE_DETAIL_FROM_MAX_API)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<
      FetchMaxAPIStoreDetailResponse,
      ActionCreatorTypes["fetchStoreDetailFromMaxAPI"]
    >,
  ) => {
    const merchantID = action.meta.previousAction.payload.merchantID

    if (merchantID !== state.maxAPIMerchantID) return state

    return update(state, {
      isFetchingMaxAPIStore: {
        $set: false,
      },
    })
  },
  [actions.FETCH_STORE_LIST]: (
    state: OrderManagerMerchantStoreState,
    action: ActionCreatorTypes["fetchStoreList"],
  ) => {
    if (action.payload.append) return state
    if (action.payload.isLoadMore)
      return update(state, {
        isFetchingMoreStoreList: { $set: true },
      })
    return update(state, {
      storeList: {
        $set: undefined,
      },
      isFetchingStoreList: { $set: true },
    })
  },
  [actionSuccess(actions.FETCH_STORE_LIST)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<FetchStoreListResponse>,
  ) => {
    const data = action?.payload?.data
    const isLoadMore = (action?.meta?.previousAction as any)?.payload.isLoadMore
    const merchants = data?.merchants
    const hasMore = !!data?.metadata?.hasMore
    const storeListUpdateSpec = !state.storeList
      ? {
          $set: merchants,
        }
      : {
          $push: merchants,
        }
    return update(state, {
      storeList: storeListUpdateSpec,
      ...(isLoadMore
        ? { isFetchingMoreStoreList: { $set: false } }
        : { isFetchingStoreList: { $set: hasMore } }),
      storeListHasMore: { $set: hasMore },
      // A store-list page that omits merchantGroupID must not clear the
      // active group — group context is not owned by this endpoint.
      merchantGroupID: {
        $set: data?.merchantGroupID || state.merchantGroupID,
      },
    })
  },
  [actionFail(actions.FETCH_STORE_LIST)]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      storeList: {
        $apply: (prev: Store[] | undefined) => {
          if (!prev) {
            return []
          }
          return prev
        },
      },
      isFetchingStoreList: { $set: false },
      isFetchingMoreStoreList: { $set: false },
    })
  },

  [actions.SEARCH_STORE]: (state: OrderManagerMerchantStoreState) => {
    return update(state, {
      isFetchingStoreSearch: { $set: true },
    })
  },
  [actionSuccess(actions.SEARCH_STORE)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<FetchStoreListResponse>,
  ) => {
    const merchants = action?.payload?.data?.merchants
    return update(state, {
      storeListSearch: {
        $set: merchants,
      },
      isFetchingStoreSearch: { $set: false },
    })
  },
  [actionFail(actions.FETCH_CITY_LIST)]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      cityList: {
        $set: undefined,
      },
      isFetchingCityList: { $set: false },
    })
  },
  [actions.FETCH_CITY_LIST]: (state: OrderManagerMerchantStoreState) => {
    return update(state, {
      isFetchingCityList: { $set: true },
    })
  },
  [actionSuccess(actions.FETCH_CITY_LIST)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<FetchCityListResponse>,
  ) => {
    const cities = action?.payload?.data?.cities
    return update(state, {
      cityList: {
        $set: cities,
      },
      isFetchingCityList: { $set: false },
    })
  },
  [actionFail(actions.SEARCH_STORE)]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      storeListSearch: {
        $set: undefined,
      },
      isFetchingStoreSearch: { $set: false },
    })
  },
  [actions.FETCH_STORE_FEATURE_FLAG]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      storeFeatureFlag: { $set: undefined },
    })
  },
  [actionSuccess(actions.FETCH_STORE_FEATURE_FLAG)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<FetchStoreListResponse>,
  ) => {
    const merchantID = (action?.meta?.previousAction as any)?.payload.merchantID
    const merchants = action?.payload?.data?.merchants?.find(
      merchant => merchant.merchantID === merchantID,
    )

    return update(state, {
      storeFeatureFlag: {
        $set: merchants?.featureFlags,
      },
    })
  },
  [actionFail(actions.FETCH_STORE_FEATURE_FLAG)]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      storeFeatureFlag: { $set: undefined },
    })
  },
  [actions.SET_STORE_FEATURE_FLAG]: (
    state: OrderManagerMerchantStoreState,
    action: ActionCreatorTypes["setStoreFeatureFlag"],
  ) => {
    const {
      isMexSupplierSeller,
      isMerchantCanEditOrder,
      enableDownloadMpaTransaction,
    } = action?.payload
    return update(state, {
      isMexSupplierSeller: { $set: isMexSupplierSeller },
      isMerchantCanEditOrder: { $set: isMerchantCanEditOrder },
      enableDownloadMpaTransaction: { $set: enableDownloadMpaTransaction },
    })
  },
  [actionSuccess(actions.FETCH_STORE_OPEN_STATUS)]: (
    state: OrderManagerMerchantStoreState,
    action: AxiosAction<
      FetchStoreOpenStatusResponse,
      ActionCreatorTypes["fetchStoreOpenStatus"]
    >,
  ) => {
    const requestedMerchantID =
      action?.meta?.previousAction?.payload?.merchantID

    if (requestedMerchantID !== state.maxAPIMerchantID) {
      return state
    }

    return update(state, {
      storeOpenStatus: {
        $set: action?.payload?.data,
      },
    })
  },

  [actions.UPDATE_STORE_STATUS]: (state: OrderManagerMerchantStoreState) => {
    return update(state, {
      isUpdatingStoreStatus: { $set: true },
    })
  },
  [actionSuccess(actions.UPDATE_STORE_STATUS)]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      isUpdatingStoreStatus: { $set: false },
    })
  },
  [actionFail(actions.UPDATE_STORE_STATUS)]: (
    state: OrderManagerMerchantStoreState,
  ) => {
    return update(state, {
      isUpdatingStoreStatus: { $set: false },
    })
  },
})
